<template>
  <div class="security-roll-call">
    <v-container class="my-5">
      <v-layout row wrap justify-center>
        <!-- Card de lectura QR -->
        <v-flex xs12 sm10 md8 class="mb-4">
          <v-card class="elevation-5">
            <v-toolbar dark color="primary">
              <v-toolbar-title>Lector QR</v-toolbar-title>
              <v-spacer></v-spacer>
              <v-btn icon color="white" flat @click="toggleCamera">
                <v-icon>{{ cameraActive ? 'videocam' : 'videocam_off' }}</v-icon>
              </v-btn>
            </v-toolbar>

            <v-card-text class="pt-4 pb-4">
              <v-layout row wrap justify-center>
                <!-- Lector QR con recuadro de ayuda visual (igual que en /access) -->
                <v-flex xs12 class="text-xs-center">
                  <div v-if="cameraActive" class="qr-reader mb-4">
                    <qrcode-stream ref="qrStream" @decode="onDecode" @init="onInit" />
                    <p class="caption grey--text mt-2">Escanea el QR del guardia</p>
                  </div>

                  <div v-else class="py-5">
                    <v-icon size="64" color="grey lighten-1" class="mb-3">videocam_off</v-icon>
                    <p class="grey--text">Cámara desactivada</p>
                  </div>
                </v-flex>

                <!-- Estado de carga y errores -->
                <v-flex xs12 v-if="qrError" class="mt-4">
                  <v-alert type="error" dismissible @input="qrError = null">
                    {{ qrError }}
                  </v-alert>
                </v-flex>

                <v-flex xs12 v-if="successMessage" class="mt-4">
                  <v-alert type="success" dismissible @input="successMessage = null">
                    {{ successMessage }}
                  </v-alert>
                </v-flex>
              </v-layout>
            </v-card-text>
          </v-card>
        </v-flex>

        <!-- Modal con información del guardia detectado -->
        <v-dialog v-model="showGuardModal" persistent max-width="500px">
          <v-card v-if="detectedGuard">
            <v-toolbar dark color="primary">
              <v-toolbar-title>Guardia Detectado</v-toolbar-title>
              <v-spacer></v-spacer>
              <v-btn icon color="white" flat @click="clearDetectedGuard">
                <v-icon>close</v-icon>
              </v-btn>
            </v-toolbar>
            <v-card-text class="pt-4">
              <v-layout row wrap justify-center align-center>
                <v-flex xs12 sm4 class="text-xs-center">
                  <v-avatar size="80" color="primary lighten-3" class="elevation-2">
                    <img v-if="detectedGuard.avatar" :src="detectedGuard.avatar" alt="Avatar">
                    <v-icon v-else size="50" color="white">person</v-icon>
                  </v-avatar>
                </v-flex>

                <v-flex xs12 sm8 class="pa-3">
                  <div class="title">{{ detectedGuard.first_name }} {{ detectedGuard.last_name }}</div>
                  <div class="body-2 grey--text mt-2">
                    <strong>Usuario:</strong> {{ detectedGuard.username }}
                  </div>
                  <div class="body-2 grey--text mt-1">
                    <strong>Placa:</strong> {{ detectedGuard.badge_number || 'N/A' }}
                  </div>
                  <v-chip
                    v-if="currentAttendanceRecord"
                    :color="currentAttendanceRecord.estado === 'entrada' ? 'warning' : 'success'"
                    text-color="white"
                    class="mt-3"
                    small
                  >
                    {{ currentAttendanceRecord.estado === 'entrada' ? 'En el turno' : 'Turno completado' }}
                  </v-chip>
                </v-flex>
              </v-layout>

              <!-- Selector de empresa (solo al registrar entrada; en salida se usa la ya asignada) -->
              <v-layout row wrap class="mt-4" v-if="!currentAttendanceRecord">
                <v-flex xs12>
                  <v-select
                    v-model="selectedCompanyId"
                    :items="activeCompanies"
                    item-text="name"
                    item-value="id"
                    label="Selecciona empresa para este turno"
                    prepend-icon="business"
                    outline
                  ></v-select>
                </v-flex>
              </v-layout>
              <v-layout row wrap class="mt-4" v-else-if="currentAttendanceRecord.estado === 'entrada'">
                <v-flex xs12>
                  <div class="body-2 grey--text">
                    <v-icon small class="mr-1">business</v-icon>
                    Empresa: <strong>{{ currentAttendanceRecord.empresa_asignada_nombre }}</strong>
                  </div>
                </v-flex>
              </v-layout>

              <v-alert v-if="qrError" type="error" dismissible class="mt-4" @input="qrError = null">
                {{ qrError }}
              </v-alert>
              <v-alert v-if="successMessage" type="success" class="mt-4">
                {{ successMessage }}
              </v-alert>
            </v-card-text>
            <v-divider></v-divider>
            <v-card-actions class="justify-center py-3">
              <v-flex xs12 sm6 class="px-2">
                <v-btn
                  block
                  color="grey darken-1"
                  dark
                  @click="clearDetectedGuard"
                >
                  Cancelar
                </v-btn>
              </v-flex>
              <v-flex xs12 sm6 class="px-2">
                <v-btn
                  block
                  :color="currentAttendanceRecord && currentAttendanceRecord.estado === 'entrada' ? 'error' : 'success'"
                  dark
                  :loading="isLoading"
                  @click="confirmAttendance"
                  :disabled="!selectedCompanyId && (!currentAttendanceRecord || currentAttendanceRecord.estado !== 'entrada')"
                >
                  <v-icon left>{{ currentAttendanceRecord && currentAttendanceRecord.estado === 'entrada' ? 'exit_to_app' : 'input' }}</v-icon>
                  {{ currentAttendanceRecord && currentAttendanceRecord.estado === 'entrada' ? 'Registrar Salida' : 'Registrar Entrada' }}
                </v-btn>
              </v-flex>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <!-- Últimas entradas/salidas registradas -->
        <v-flex xs12 sm10 md8>
          <v-card class="elevation-2">
            <v-toolbar color="grey lighten-2">
              <v-toolbar-title>Últimas Actividades (Hoy)</v-toolbar-title>
            </v-toolbar>
            <v-card-text class="pa-0">
              <v-list v-if="todayAttendance.length > 0" two-line>
                <v-list-tile v-for="attendance in todayAttendance" :key="attendance.id" class="border-bottom">
                  <v-list-tile-avatar>
                    <img v-if="attendance.foto_entrada" :src="attendance.foto_entrada" :alt="attendance.guardia_nombre">
                    <v-icon v-else color="grey lighten-1">person</v-icon>
                  </v-list-tile-avatar>

                  <v-list-tile-content>
                    <v-list-tile-title>{{ attendance.guardia_nombre }}</v-list-tile-title>
                    <v-list-tile-sub-title>
                      {{ attendance.empresa_nombre }} • Entrada: {{ attendance.hora_entrada }}
                      <span v-if="attendance.hora_salida"> • Salida: {{ attendance.hora_salida }}</span>
                    </v-list-tile-sub-title>
                  </v-list-tile-content>

                  <v-list-tile-action>
                    <v-chip
                      :color="attendance.estado === 'entrada' ? 'warning' : 'success'"
                      text-color="white"
                      small
                    >
                      {{ attendance.estado === 'entrada' ? 'En turno' : 'Completado' }}
                    </v-chip>
                  </v-list-tile-action>
                </v-list-tile>
              </v-list>
              <div v-else class="pa-4 text-xs-center grey--text">
                Sin registros hoy
              </div>
            </v-card-text>
          </v-card>
        </v-flex>
      </v-layout>
    </v-container>
  </div>
</template>

<script>
import { QrcodeStream } from 'vue-qrcode-reader'
import { securityService } from '@/services/securityService'

export default {
  name: 'SecurityRollCall',
  components: {
    QrcodeStream
  },
  data() {
    return {
      cameraActive: false,
      showGuardModal: false,
      detectedGuard: null,
      currentAttendanceRecord: null,
      selectedCompanyId: null,
      capturedPhoto: null,
      qrError: null,
      successMessage: null,
      attendance: [],
      activeCompanies: [],
      isLoading: false
    }
  },
  computed: {
    todayAttendance() {
      return [...this.attendance]
        .sort((a, b) => new Date(`2000-01-01 ${b.hora_entrada}`) - new Date(`2000-01-01 ${a.hora_entrada}`))
    }
  },
  methods: {
    toggleCamera() {
      this.cameraActive = !this.cameraActive
      this.qrError = null
    },
    onInit(promise) {
      promise.catch(() => {
        this.qrError = 'No se pudo acceder a la cámara. Verifica los permisos.'
        this.cameraActive = false
      })
    },
    // Captura una foto del video en el momento del escaneo, usada como evidencia de entrada/salida
    capturePhoto() {
      return new Promise((resolve) => {
        const streamComponent = this.$refs.qrStream
        const videoEl = streamComponent && streamComponent.$el && streamComponent.$el.querySelector('video')
        if (!videoEl || !videoEl.videoWidth) {
          resolve(null)
          return
        }
        const canvas = document.createElement('canvas')
        canvas.width = videoEl.videoWidth
        canvas.height = videoEl.videoHeight
        canvas.getContext('2d').drawImage(videoEl, 0, 0, canvas.width, canvas.height)
        canvas.toBlob(blob => resolve(blob), 'image/jpeg', 0.85)
      })
    },
    async onDecode(qrData) {
      if (this.detectedGuard && this.detectedGuard.unique_code === qrData) {
        return // Ya está detectado
      }

      this.isLoading = true
      this.qrError = null

      const photoBlob = await this.capturePhoto()

      const result = await securityService.getGuardByQR(qrData)

      if (!result.success) {
        this.qrError = result.error || 'QR no registrado'
        this.isLoading = false
        return
      }

      this.cameraActive = false
      this.detectedGuard = result.data
      this.capturedPhoto = photoBlob

      // Obtener registro de hoy para este guardia
      const todayResult = await securityService.getTodayAttendance(result.data.id)
      if (todayResult.success) {
        this.currentAttendanceRecord = todayResult.data
      }

      // En salida se reutiliza la empresa ya asignada en la entrada, sin volver a seleccionarla
      this.selectedCompanyId = this.currentAttendanceRecord ? this.currentAttendanceRecord.empresa_asignada_id : null
      this.isLoading = false
      this.showGuardModal = true
    },
    clearDetectedGuard() {
      this.showGuardModal = false
      this.detectedGuard = null
      this.currentAttendanceRecord = null
      this.selectedCompanyId = null
      this.capturedPhoto = null
      this.cameraActive = true
    },
    async loadTodayAttendance() {
      const today = new Date().toISOString().split('T')[0]
      const result = await securityService.getDailyAttendance({ date: today })

      if (result.success && result.data) {
        this.attendance = result.data.map(item => ({
          id: item.attendance_id,
          guardia_nombre: item.guardia_nombre,
          guardia_badge: item.guardia_badge,
          empresa_nombre: item.empresa_asignada_nombre,
          hora_entrada: item.entrada ? item.entrada.hora : null,
          hora_salida: item.salida ? item.salida.hora : null,
          foto_entrada: item.entrada ? item.entrada.foto : null,
          foto_salida: item.salida ? item.salida.foto : null,
          total_horas: item.total_horas,
          estado: item.estado
        }))
      }
    },
    async loadCompanies() {
      const result = await securityService.getActiveCompanies()
      if (result.success) {
        this.activeCompanies = result.data
      }
    },
    async confirmAttendance() {
      if (!this.detectedGuard) return

      this.isLoading = true
      this.qrError = null

      try {
        if (!this.currentAttendanceRecord) {
          // Registrar entrada
          if (!this.selectedCompanyId) {
            this.qrError = 'Debes seleccionar una empresa'
            this.isLoading = false
            return
          }

          const result = await securityService.registerEntrada(
            this.detectedGuard.id,
            this.selectedCompanyId,
            this.capturedPhoto
          )

          if (!result.success) {
            this.qrError = result.error || 'Error al registrar entrada'
            this.isLoading = false
            return
          }

          this.currentAttendanceRecord = result.data
          this.successMessage = result.message || 'Entrada registrada correctamente'
        } else {
          // Registrar salida
          const result = await securityService.registerSalida(
            this.currentAttendanceRecord.id,
            this.capturedPhoto
          )

          if (!result.success) {
            this.qrError = result.error || 'Error al registrar salida'
            this.isLoading = false
            return
          }

          this.currentAttendanceRecord = result.data
          this.successMessage = result.message || 'Salida registrada correctamente'
        }

        // Recargar actividades del día
        await this.loadTodayAttendance()

        setTimeout(() => {
          this.clearDetectedGuard()
          this.successMessage = null
        }, 2000)
      } finally {
        this.isLoading = false
      }
    }
  },
  mounted() {
    this.loadCompanies()
    this.loadTodayAttendance()
  }
}
</script>

<style scoped>
.qr-reader {
  position: relative;
  width: 100%;
}

.border-bottom {
  border-bottom: 1px solid #e0e0e0;
}
</style>
